
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import NewRegisterBtn from '../../../../../components/Buttons/NewRegisterBtn';
import AsyncTable, { Column } from '../../../../../components/AsyncTable';
import getGenericRequestPageForAsyncTable from '../../../../../utils/TablesFunctions';
import { EditIcon } from '../../../../../icons';
import { ActionButtonsContainer } from '../../../../../components/TablePaginator/styles';

import { PersistedDatasync } from '../../../../../types/apiResponse/datasync';
import { datasyncAPI } from '../../../../../services/requests';
import DatasyncModal from '../../Components/CreateOrEditDatasyncModal/CreateOrEditDatasyncModal';

const DatasyncConfigPage: React.FC = () => {
  const [tableData, setTableData] = useState<PersistedDatasync[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDatasync, setSelectedDatasync] = useState<PersistedDatasync | null>(null);
  const theme = useTheme();
  const { t } = useTranslation();

  const handleEdit = (row: PersistedDatasync) => {
    setSelectedDatasync(row);
    setShowEditModal(true);
  };

  const handleSuccess = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedDatasync(null);
  };

  const actionsButtons = (row: PersistedDatasync) => (
    <ActionButtonsContainer type={4}>
      <EditIcon title={t('edit')} onClick={() => handleEdit(row)} />
    </ActionButtonsContainer>
  );

  const columns: Column<PersistedDatasync>[] = [
    {
      accessor: 'active',
      label: t('active'),
      Cell: (row: PersistedDatasync) => (
        <span
          style={{
            color: row.active ? theme.colors.positive : theme.colors.negative,
          }}
        >
          {row.active ? t('yes') : t('no')}
        </span>
      ),
    },
    {
      accessor: 'type',
      label: t('table'),
      Cell: (row: PersistedDatasync) => row.type?.table || '-',
    },
    {
      accessor: 'type',
      label: t('label'),
      Cell: (row: PersistedDatasync) => t(row.type?.label as string) || '-',
    },
    { accessor: 'datasync_type', label: t('type') },
    { accessor: 'interval_in_minutes', label: t('intervalInMinutes') },
    { accessor: 'timeout_in_seconds', label: `${t('timeout')} (s)` },
    { accessor: 'updated_at', label: t('updatedAt') },
    {
      Cell: actionsButtons,
    },
  ];

  return (
    <div>
      <NewRegisterBtn
        onClick={() => setShowCreateModal(true)}
        style={{ marginLeft: '0', marginBottom: '1rem' }}
      />

      <AsyncTable
        tableName="datasyncs"
        columns={columns}
        value={tableData}
        onChange={setTableData}
        requestPage={getGenericRequestPageForAsyncTable(datasyncAPI)}
        reqListener={[refreshTrigger]}
        options={{
          styles: {
            primaryColor: `${theme.colors.surface}`,
            secondaryColor: `${theme.colors.onSurface}`,
            alternateRowColor: theme.colors.onSurfaceLight,
            textColor: theme.colors.text,
          },
          quantityPerPageLabel: t('quantityPerPage'),
          searchPlaceholder: t('search'),
          totalPageLabel: `${t('totalPages')}: `,
        }}
      />

      <DatasyncModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleSuccess}
      />

      <DatasyncModal
        open={showEditModal}
        onClose={handleCloseEditModal}
        onSuccess={handleSuccess}
        datasync={selectedDatasync}
      />
    </div>
  );
};

export default DatasyncConfigPage;
